<template>
  <div>
    <v-row no-gutters align="center" class="mb-3">
      <div class="text-subtitle-1 font-weight-medium">{{ $t("finance_commissions_title") }}</div>
    </v-row>

    <v-row class="mb-3">
      <v-col v-for="kpi in kpis" :key="kpi.label" cols="6" sm="4">
        <v-card outlined class="pa-3 fill-height">
          <div class="text-caption grey--text d-flex align-center" style="gap: 4px">
            {{ kpi.label }}
            <v-tooltip v-if="kpi.tooltip" top>
              <template #activator="{ on, attrs }">
                <v-icon v-bind="attrs" v-on="on" size="14">mdi-information-outline</v-icon>
              </template>
              <span>{{ kpi.tooltip }}</span>
            </v-tooltip>
          </div>
          <div class="text-h6 font-weight-bold">{{ kpi.value }}</div>
        </v-card>
      </v-col>
    </v-row>

    <v-card outlined>
      <v-data-table
        :headers="commissionsHeaders"
        :items="rows"
        :loading="loading"
        :loading-text="$t('finance_loading')"
        mobile-breakpoint="0"
        :no-data-text="$t('finance_no_commission_items')"
        item-key="orderId"
        class="striped-table"
        @click:row="openEntry"
      >
        <template v-slot:item.orderId="{ item }">
          <span class="font-weight-bold">{{ item.orderId }}</span>
        </template>
        <template v-slot:item.date="{ item }">{{ localeDate(item.date) }}</template>
        <template v-slot:item.turnover="{ item }">{{ formatCurrency(item.turnover) }}</template>
        <template v-slot:item.rate="{ item }">{{ item.rate }}%</template>
        <template v-slot:item.commission="{ item }">{{ formatCurrency(item.commission) }}</template>
      </v-data-table>
      <p v-if="!loading && rows.length > 0" class="hint-copy px-4 pb-3">
        {{ $t("finance_click_order_commission_hint") }}
      </p>
    </v-card>

    <!-- Detail dialog - samo za čitanje, provizije se obračunavaju automatski na backendu -->
    <v-dialog v-model="detailOpen" max-width="400">
      <v-card v-if="selectedEntry">
        <v-card-title>{{ selectedEntry.orderId }}</v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <div class="detail-row">
            <span>{{ $t("finance_date") }}</span>
            <strong>{{ localeDate(selectedEntry.date) }}</strong>
          </div>
          <div class="detail-row">
            <span>{{ $t("finance_turnover") }}</span>
            <strong>{{ formatCurrency(selectedEntry.turnover) }}</strong>
          </div>
          <div class="detail-row">
            <span>{{ $t("finance_commission_with_rate", { rate: selectedEntry.rate }) }}</span>
            <strong>{{ formatCurrency(selectedEntry.commission) }}</strong>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import restaurantFinanceService from "@/services/restaurantFinance";
import hasSnackbar from "@/mixins/hasSnackbar";

export default {
  name: "FinanceTabCommissions",

  mixins: [hasSnackbar],

  data: () => ({
    loading: true,
    contractedRate: null,
    chargedCommission: 0,
    avgRate: 0,
    rows: [],
    detailOpen: false,
    selectedOrderId: null,
  }),

  computed: {
    restaurantId() {
      return this.$store.state.restaurant?.id;
    },

    commissionsHeaders() {
      return [
        { text: this.$t("finance_order"), value: "orderId" },
        { text: this.$t("finance_date"), value: "date" },
        { text: this.$t("finance_turnover"), value: "turnover", align: "end" },
        { text: "%", value: "rate", align: "end" },
        { text: this.$t("finance_commission"), value: "commission", align: "end" },
      ];
    },

    selectedEntry() {
      return this.rows.find((e) => e.orderId === this.selectedOrderId) || null;
    },

    kpis() {
      return [
        {
          label: this.$t("finance_agreed_commission"),
          value: this.contractedRate != null ? `${this.contractedRate}%` : "—",
        },
        {
          label: this.$t("finance_charged_commission"),
          value: this.formatCurrency(this.chargedCommission),
        },
        {
          label: this.$t("finance_average_abbr"),
          value: `${Number(this.avgRate || 0).toFixed(1)}%`,
          tooltip: this.$t("finance_avg_rate_tooltip"),
        },
      ];
    },
  },

  created() {
    this.load();
  },

  methods: {
    formatCurrency(value) {
      return `${Number(value || 0).toFixed(2)} ${this.restaurantCurrency}`;
    },

    openEntry(item) {
      this.selectedOrderId = item.orderId;
      this.detailOpen = true;
    },

    async load() {
      if (!this.restaurantId) return;

      this.loading = true;
      try {
        const { data } = await restaurantFinanceService.getCommissions(this.restaurantId);
        const payload = data?.data || {};
        this.contractedRate =
          payload.contracted_rate != null ? Number(payload.contracted_rate) : null;
        this.chargedCommission = Number(payload.charged_commission) || 0;
        this.avgRate = Number(payload.avg_rate) || 0;
        this.rows = (payload.rows || []).map((row) => ({
          orderId: row.order_id,
          turnover: Number(row.turnover) || 0,
          rate: Number(row.rate) || 0,
          commission: Number(row.commission) || 0,
          date: row.date,
        }));
      } catch (error) {
        this.showErrorSnackbar(this.$t("finance_commissions_load_error"));
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.hint-copy {
  margin: 0;
  font-size: 0.78rem;
  color: #9aa4b2;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  font-size: 0.88rem;
  color: #6b7685;
  padding: 4px 0;
}
</style>
